import { LitElement, createRef, css, html, nothing, ref } from '@lit'
import { storefrontClient } from '@nova/storefront'
import styles from '@nova/styles'
import { formatPrice, t } from '@nova/utils'

class NovaMiniCart extends LitElement {
  static properties = {
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
    compareAtPrices: {
      type: Object,
      state: true,
    },
    continueSell: {
      type: Object,
      state: true,
    },
    maxValues: {
      type: Object,
      state: true,
    },
  }

  $cartDrawer = createRef()
  $itemContainer = createRef()

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.final_price) * current.quantity,
      0,
    )
  }

  get formattedSubtotal() {
    return formatPrice(this.subtotal)
  }

  async queryVariantData(items) {
    const compareAtPrices = {}
    const continueSell = {}
    const maxValues = {}

    await Promise.all(
      items.map(async (item) => {
        const data = await this.getVariantData(item)
        if (!data) return
        compareAtPrices[item.key] = data.compareAtPrice
        continueSell[item.key] = data.inventoryPolicy
        maxValues[item.key] = data.max
      }),
    )

    this.compareAtPrices = compareAtPrices
    this.continueSell = continueSell
    this.maxValues = maxValues
  }

  getVariantData = async (currentItem) => {
    let compareAtPrice = 0
    let max
    let inventoryPolicy
    const matchedItem = this.initialItems.find(
      (item) => item.key === currentItem.key,
    )

    if (matchedItem) {
      compareAtPrice = Number(matchedItem.compare_at_price) || 0
      max = matchedItem.inventory_quantity
      inventoryPolicy = matchedItem.inventory_policy === 'continue'
    }

    // /cart.js has no compare_at_price; resolve from Storefront or product.js
    if (!compareAtPrice && currentItem.variant_id) {
      try {
        const variantData = await storefrontClient.getVariantById(
          currentItem.variant_id,
        )
        if (variantData) {
          compareAtPrice = Number(variantData.compareAtPrice) || 0
          max = max ?? variantData.quantityAvailable
          inventoryPolicy =
            inventoryPolicy ?? variantData.currentlyNotInStock
        }
      } catch {
        // Fall through to product.js
      }
    }

    if (!compareAtPrice && currentItem.handle && currentItem.variant_id) {
      try {
        const response = await fetch(
          `/products/${currentItem.handle}.js`,
        )
        if (response.ok) {
          const product = await response.json()
          const variant = product.variants?.find(
            (entry) => Number(entry.id) === Number(currentItem.variant_id),
          )
          compareAtPrice = Number(variant?.compare_at_price) || 0
        }
      } catch {
        // Leave compare_at at 0 when lookup fails
      }
    }

    return {
      compareAtPrice,
      max,
      inventoryPolicy,
    }
  }

  constructor() {
    super()
    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
    this.initialItems = []
    this.items = []
    this.compareAtPrices = {}
    this.continueSell = {}
    this.maxValues = {}
  }

  connectedCallback() {
    super.connectedCallback()
    this.refresh()
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  _handleOpenCart = async () => {
    await this.refresh()
    await this.updateComplete
    this.$cartDrawer.value?.show()
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value?.hide()
  }

  handleFocusAfterItemRemove() {
    const removedLineItemIndex = localStorage.getItem('removeLineItemIndex')
    if (!removedLineItemIndex || !this.$itemContainer.value) return
    const index = Math.max(0, removedLineItemIndex - 1)
    const lineItems =
      this.$itemContainer.value.querySelectorAll('nova-cart-item')
    if (lineItems.length < 1 || !lineItems[index]) return
    lineItems[index].shadowRoot
      .querySelector('.js-cart-item-title')
      ?.focus({ focusVisible: true })
    localStorage.removeItem('removeLineItemIndex')
  }

  refresh = async () => {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.items = data.items
      this.queryVariantData(this.items)
      this.handleFocusAfterItemRemove()
    } catch {
      console.error('Error fetching cart data')
    }
  }

  willUpdate(changedProperties) {
    if (
      changedProperties.has('initialItems') &&
      this.initialItems?.length &&
      !this.items.length
    ) {
      this.items = this.initialItems
      this.queryVariantData(this.items)
    }
    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:items-count', {
          detail: { count: this.count },
        }),
      )
    }
  }

  render() {
    return html`
      <sl-drawer label="${window.cartStrings.title}" class="drawer" ${ref(this.$cartDrawer)}>
        ${
          !this.items.length
            ? html`
              <div class="empty-state">
                <p>${window.cartStrings.empty}</p>
                <sl-button @click=${this._handleCloseCart}>
                  ${window.cartStrings.continueShopping}
                </sl-button>
                <div></div>
              </div>
            `
            : html`
              ${
                __NOVA_GLOBAL__.showFreeShippingProgressBar === true
                  ? html`<nova-free-shipping-threshold></nova-free-shipping-threshold>`
                  : nothing
              }
              <div class="items" ${ref(this.$itemContainer)}>
                ${this.items.map(
                  (item) =>
                    html`<nova-cart-item .item=${item} .compareAtPrice=${Number(this.compareAtPrices[item.key]) || 0} .inventoryPolicy=${this.continueSell[item.key] || false} .max=${this.maxValues[item.key] || ''}></nova-cart-item>`,
                )}
              </div>
              <div slot="footer" class="summary">
                <span class="summary__total">
                  <p>${window.cartStrings.subtotal}</p>
                  <p>${this.formattedSubtotal}</p>
                </span>
                <sl-button href="/checkout">${window.cartStrings.checkout}</sl-button>
                <slot name="govx" class="summary__govx-slot"></slot>
                <p class="summary__terms">${window.cartStrings.terms}</p>
              </div>
            `
        }
      </sl-drawer>
    `
  }

  static styles = [
    styles.flex,
    styles.variables,
    css`
      :host {
        display: block;
        position: relative;
        z-index: var(--nova-z-index-drawer, 700);
      }
      .drawer {
        --size: 100%;
        --header-spacing: 30px 30px 20px 30px;
        --body-spacing: 0px 30px;
        --footer-spacing: 0;
      }
      .drawer::part(overlay) {
        fill: var(--nova-utility-overlay, rgba(0, 0, 0, 0.5));
      }
      .drawer::part(panel) {
        background-color: #EDEDED;
      }
      .drawer::part(header) {
        align-items: center;
        height: fit-content;
        padding: var(--nova-space-md);
        position: relative;
      }
      .drawer::part(title) {
        font: var(--nova-h6-font);
        font-weight: 500;
        line-height: 150%;
        letter-spacing: var(--nova-h6-letter-spacing);
        padding: 0;
				text-transform: uppercase;
      }
      .drawer::part(header-actions) {
        padding: 0;
      }
      .drawer::part(close-button) {
        width: var(--nova-icon-size-click);
        height: var(--nova-icon-size-click);
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        right: calc(var(--nova-space-md) - ((var(--nova-icon-size-click) - var(--nova-icon-size-m)) / 2));
        top: 50%;
        transform: translateY(-50%);
      }
      .drawer::part(close-button__base) {
        color: var(--nova-brand-ink);
        padding: 0;
        font-size: var(--nova-icon-size-m);
      }
      .drawer::part(body) {
        padding: calc(var(--nova-space-lg) - var(--nova-space-md)) var(--nova-space-md) var(--nova-space-md);
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        gap: var(--nova-space-sm);
      }
      .empty-state p {
        line-height: 140%;
        margin: 0;
      }
      sl-button {
        display: flex;
      }
      sl-button::part(base) {
        background-color: var(--nova-brand-primary);
        color: var(--nova-brand-reverse);
        height: auto;
        min-height: unset;
        width: 100%;
        border: none;
        border-radius: var(--nova-border-radius-l);
      }
      sl-button::part(label) {
        line-height: 33px;
        font-weight: 700;
        letter-spacing: 1.4px;
        padding: var(--nova-space-xs) var(--nova-space-sm);
        margin: 0;
				text-transform: uppercase;
      }
      @media screen and (min-width: 768px) {
        .drawer {
          --size: 500px;
        }
        .drawer::part(body) {
          padding: 0 var(--nova-space-md) var(--nova-space-lg);
        }
        sl-button::part(label) {
          padding: var(--nova-space-xs) var(--nova-space-md);
        }
      }
      .summary {
        background: var(--nova-brand-light);
        padding: var(--nova-space-md);
        font: var(--nova-body-font)
        line-height: 140%;
      }
      .summary__total {
        display: flex;
        justify-content: space-between;
        margin-bottom: var(--nova-space-sm);
      }
      .summary__total p {
        margin: 0;
      }
      .summary__govx-slot {
        display: block;
        width: 100%;
        margin-top: var(--nova-space-xs);
      }
      .summary__terms {
        color: var(--nova-brand-subdued);
        text-align: center;
        font: var(--nova-body-caption);
        line-height: 130%;
        margin: var(--nova-space-sm) 0 0;
      }
      @media screen and (min-width: 768px) {
        .summary__total {
          margin-bottom: var(--nova-space-xs);
        }
      }
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)
